import React, { useMemo, useState } from 'react';
import BasicButton from './BasicButton';
import TextInput from './TextInput';
import CheckBox from './CheckBox';
import EndPoint from 'Api/EndPoint';
import { joinError, WRONG_FORMAT_EMAIL } from 'Api/Errors';
import { useMutationQuery } from 'Api/hooks/useMutationQuery';
import { useSelector } from 'react-redux';
import VerifiedPhoneInput from './VerifiedPhoneInput';
import { useError } from 'Api/hooks/useError';
import { useIntl } from 'react-intl';
const DEBUG = import.meta.env.DEV;

const initData = () => ({
  password: '',
  managerName: '',
  agreePrivacyUsage: false,
  referral: '',
  email: '',
});

const handlePolicyClick = locale => {
  const width = window.outerWidth / 1.5;
  const height = window.outerHeight / 1.5;
  const left = window.screenX + window.outerWidth / 6;
  const top = window.screenY + window.outerHeight / 6;
  window.open(
    `${EndPoint.HostRoot}/terms/${locale}/t3/`,
    'Policy',
    `location=no,toolbar=no,scrollbars=no,resizable=yes,status=no,menubar=no,width=${width}px,height=${height}px,top=${top}px,left=${left}px`
  );
};

const isBlank = value => value.trim() === '';

const defaultData = {
  isTester: DEBUG,
  agreeMarketing: false,
  joinFrom: 'SELF_WEB',
  businessType: 'FREELANCER',
};

const FreelancerJoinForm = ({ onCompleted, managerPhone, otpCode }) => {
  const { showErrorPopup } = useError();
  const { locale } = useSelector(state => state.settings.locale);
  const { country } = useSelector(state => state.settings);
  const [data, setData] = useState(initData);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const { password, managerName, referral, email, agreePrivacyUsage } = data;
  const phoneCountry = useMemo(() => (country === 'ko' ? '82' : '84'), [country]);
  const { mutate } = useMutationQuery({
    method: 'post',
    url: '/auth/join',
    options: {
      onSuccess: () => {
        onCompleted({ phone: managerPhone, password, phoneCountry, companyName: managerName });
      },
      onError: error => {
        showErrorPopup({ text: error?.response?.data?.message });
      },
    },
  });
  const { formatMessage } = useIntl();

  const handleDataChange = ({ target: { name, value } }) => {
    setError(null);
    setData(data => ({ ...data, [name]: value }));
  };

  const isEmail = value => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleJoin = () => {
    if (email && !isEmail(email)) return setError(WRONG_FORMAT_EMAIL);

    mutate({
      ...defaultData,
      otpCode,
      phoneCountry,
      businessNumber: managerPhone,
      companyName: managerName,
      managerName,
      managerPhone,
      agreePrivacyUsage,
      password,
      ...(referral && { referral }),
      ...(email && { email }),
    });
  };

  const isDisabled = useMemo(() => {
    if (password.length < 6 || password.length > 15) return true;
    if (password !== confirmPassword) return true;

    return isBlank(managerName) || !agreePrivacyUsage;
  }, [managerName, password, confirmPassword, agreePrivacyUsage]);

  const isError = (myErrorList = []) => {
    if (!error) return false;
    if (myErrorList.includes(error)) return true;
    return false;
  };
  return (
    <>
      <div className="form-container">
        <VerifiedPhoneInput value={managerPhone} />
        <TextInput name="managerName" label="signIn.join.managerName2" placeholder="signIn.join.managerName.placeholder" value={managerName} onChange={handleDataChange} tabIndex={3} />
        <TextInput
          isError={password !== '' && (password.length < 6 || password.length > 15)}
          errorText={formatMessage({ id: 'error.password.validate' })}
          name="password"
          label="common.password"
          placeholder="sign.password.message"
          value={password}
          onChange={handleDataChange}
          isPassword
          tabIndex={4}
        />
        <TextInput
          isError={confirmPassword !== '' && confirmPassword !== password}
          errorText={formatMessage({ id: 'error.password.notMatch' })}
          name="confirmPassword"
          label="common.password2"
          value={confirmPassword}
          onChange={e => {
            setConfirmPassword(e.target.value);
            setError(null);
          }}
          isPassword
          tabIndex={5}
        />
        <TextInput
          isError={isError([WRONG_FORMAT_EMAIL])}
          errorText={joinError[error] && formatMessage({ id: joinError[error] })}
          name="email"
          label="signIn.join.email"
          placeholder="signIn.join.email.placeholder"
          value={email}
          onChange={handleDataChange}
          tabIndex={6}
        />
        <TextInput name="referral" label="signIn.join.referral" placeholder="signIn.join.referral.placeholder" value={referral} onChange={handleDataChange} tabIndex={7} />
      </div>
      <CheckBox
        label="signIn.join.agreePrivacyUsage"
        isChecked={agreePrivacyUsage}
        onSetIsChecked={() => handleDataChange({ target: { name: 'agreePrivacyUsage', value: !agreePrivacyUsage } })}
        onClickExtra={() => handlePolicyClick(locale)}
      />
      <BasicButton label="signIn.join.button" onClick={handleJoin} style={{ backgroundColor: '#0567F9' }} disabled={isDisabled} />
    </>
  );
};

export default FreelancerJoinForm;
